import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import { ROUTER } from "../../../common/constants";
import { ENQUIRY_CENTRE_ORIGIN, EXTERNAL_APP_URI } from "../../../common/uri/property-uri.constants";
import { storeModules } from "../../../store";
import { TAB_VIEW_GETTERS } from "../../../store/tab-view/tab-view-types.store";
import { TabViewModel } from "../../../models/common/tab-view.model";
import { embeddedAppTokenBroker } from "../../../utils/embedded-app-token";
import { LanguageHelper } from "../../../utils/language-helper";
import KeepAliveIframe from "../../../components/keep-alive-iframe/KeepAliveIframe.vue";
import { ENQUIRY_KEEP_ALIVE, ENQUIRY_KEEP_ALIVE_PROTOCOL } from "../enquiry-events.constants";

const TabViewGetter = namespace(storeModules.TabView, Getter);

export const ENQUIRY_CENTRE_IFRAME_ID = "enquiryCentreIframe";

// The single Enquiry iframe, for the route component's message handling.
export function getEnquiryCentreIframe(): HTMLIFrameElement {
	return document.getElementById(ENQUIRY_CENTRE_IFRAME_ID) as HTMLIFrameElement;
}

interface EnquiryFrameInstance {
	key: number;
	src: string;
}

// Persistent host for the Enquiry Centre iframe, mounted once in the app shell
// so tab switches (route changes) hide it instead of destroying it. Owns the
// iframe's lifecycle: lazy create on first visit, destroy on tab close, idle
// release (non-dirty).
@Component({
	template: require("./template.html"),
	components: { "keep-alive-iframe": KeepAliveIframe }
})
export default class EnquiryCentreFrame extends Vue {
	@TabViewGetter(TAB_VIEW_GETTERS.GET_TAB_VIEWS) tabViews: TabViewModel[];

	instance: EnquiryFrameInstance = null;

	readonly origin = ENQUIRY_CENTRE_ORIGIN;
	readonly protocol = ENQUIRY_KEEP_ALIVE_PROTOCOL;
	readonly idleReleaseMs = ENQUIRY_KEEP_ALIVE.IDLE_RELEASE_MS;
	readonly dirtyQueryTimeoutMs = ENQUIRY_KEEP_ALIVE.DIRTY_QUERY_TIMEOUT_MS;
	readonly frameId = ENQUIRY_CENTRE_IFRAME_ID;

	private instanceCounter = 0;
	private unregisterAccessToken: () => void = null;

	mounted() {
		this.unregisterAccessToken = embeddedAppTokenBroker.register({
			origin: ENQUIRY_CENTRE_ORIGIN,
			getContentWindow: () => getEnquiryCentreIframe()?.contentWindow || null,
			getLanguageId: () => LanguageHelper.convertLanguageInt(this.$i18n.locale)
		});
	}

	beforeDestroy() {
		this.unregisterAccessToken?.();
	}

	get isEnquiryRoute(): boolean {
		return this.$route.name === ROUTER.ENQUIRY_CENTRE;
	}

	get hasEnquiryTab(): boolean {
		return (this.tabViews || []).some(tab => tab.identifyData?.name === ROUTER.ENQUIRY_CENTRE);
	}

	@Watch("isEnquiryRoute", { immediate: true })
	onEnquiryRouteChanged(active: boolean) {
		if (active && !this.instance) this.createInstance();
	}

	@Watch("hasEnquiryTab")
	onEnquiryTabChanged(hasTab: boolean) {
		if (!hasTab) this.destroyInstance();
	}

	destroyInstance() {
		this.instance = null;
	}

	private createInstance() {
		const language = LanguageHelper.convertToWebviewLang(LanguageHelper.convertLanguageInt(this.$i18n.locale));
		this.instance = {
			key: ++this.instanceCounter,
			src: `${EXTERNAL_APP_URI.ENQUIRY_CENTRE_APP}?language=${language}`
		};
	}
}
